import { useGetMyPtStudentsLookup } from "@services/parentTeacher/students";
import { createContext, Dispatch, PropsWithChildren, SetStateAction, useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { hgColor } from "./grading/theme";
import ChipSelectorStrip from "@components/shared/ui/ChipSelectorStrip";
import { useReactiveVar } from "@utils/reactiveVar";
import { curriculumsVar } from "../../../reactiveVars/coursemanagementVars";
import { useAuth } from "../../../context/AuthContext";

export interface PtChild {
  id: string;
  first_name: string | null;
  last_name: string | null;
  name: string;
  school_year_name: string | null;
  grade_level_name: string | null;
  grade_level_id: string | null;
  curriculum_id: string | null;
  curriculum_name: string | null;
}

export interface ParentTeacherContextProps {
  student: string | null;
  setStudent: Dispatch<SetStateAction<string | null>>;
  allowedStudentIds: string[];
  children: PtChild[];
  selectedChild: PtChild | null;
  familyLabel: string;
  /**
   * True while the PTA child lookup is still in flight. OPTIONAL so every
   * existing construction of this value (tests, ad-hoc providers) keeps
   * type-checking. Consumers use it to avoid rendering a "you have no
   * children" empty state over a list that simply has not arrived yet — the
   * grading screen showed exactly that lie before the 2026-09-21 hotfix.
   */
  childrenLoading?: boolean;
}

export const ParentTeacherContext = createContext<ParentTeacherContextProps>({
  student: null,
  setStudent: () => { },
  allowedStudentIds: [],
  children: [],
  selectedChild: null,
  familyLabel: 'My family',
  childrenLoading: false,
});

export const ParentTeacherContextProvider = ({ children: reactChildren }: PropsWithChildren) => {

  const { student_id } = useParams<{ student_id: string }>();
  const [student, setStudent] = useState<string | null>(student_id ?? null);

  const { profile } = useAuth();
  // PT-scoped via parent_teacher_assignments. Exposing the allowed-id
  // set lets profile pages guard against direct-URL navigation to a
  // student outside this PT's caseload (2026-05-13 scoping fix).
  const { rows, loading: childrenLoading } = useGetMyPtStudentsLookup();
  const curriculums = useReactiveVar(curriculumsVar);

  const childList = useMemo<PtChild[]>(() => rows.map((r) => ({
    id: r.id,
    first_name: r.first_name ?? null,
    last_name: r.last_name ?? null,
    name: `${r.first_name ?? ''} ${r.last_name ?? ''}`.trim() || 'Child',
    school_year_name: r.school_year_name ?? null,
    grade_level_name: r.grade_level_name ?? null,
    grade_level_id: r.grade_level_id ?? null,
    curriculum_id: r.curriculum_id ?? null,
    curriculum_name: (curriculums as Array<{ id?: string; curriculum_id?: string; curriculum_name?: string }>)
      .find((c) => c.id === r.curriculum_id || c.curriculum_id === r.curriculum_id)?.curriculum_name ?? null,
  })), [rows, curriculums]);

  const allowedStudentIds = useMemo(() => childList.map((c) => c.id), [childList]);

  // Auto-select the first child when none is selected yet.
  useEffect(() => {
    if (!student && childList.length > 0) setStudent(childList[0].id);
  }, [student, childList]);

  const selectedChild = useMemo(
    () => childList.find((c) => c.id === student) ?? null,
    [childList, student],
  );

  const familyLabel = profile?.last_name ? `The ${profile.last_name} family` : 'My family';

  return (
    <ParentTeacherContext.Provider value={{
      student,
      setStudent,
      allowedStudentIds,
      children: childList,
      selectedChild,
      familyLabel,
      childrenLoading,
    }}>
      {reactChildren}
    </ParentTeacherContext.Provider>
  );
}

// CONTEXT COMPONENTS
export const StudentSelector = () => {
  const { student, setStudent, children, childrenLoading } = useContext(ParentTeacherContext);

  const items = children.map((child) => ({
    id: child.id,
    label: `${child.first_name ?? ''} ${child.last_name ?? ''}`.trim() || 'Student',
  }));

  // Swipe-strip of child chips (the grade-page pattern), not a dropdown —
  // consistent across the PT workspace and mobile-friendly for a handful of
  // children. Drives ParentTeacherContext.student for Students/Courses/Assessments.
  return (
    <ChipSelectorStrip
      items={items}
      selectedId={student}
      onSelect={(id) => setStudent(id)}
      loading={childrenLoading ?? false}
      emptyText="No students assigned yet."
      ariaLabel="Select Student"
      getColor={(item) => hgColor(item.id)}
      testIdPrefix="pt-student-chip"
    />
  );
}
